import { useApolloClient } from "@apollo/react-hooks"
import { Next, Error, CompletedResult, Config, Extra } from "qiniu-js"
import * as qiniu from "qiniu-js"
import gql from "graphql-tag"
import { useContext, useEffect, useMemo, useState } from "react"
import { cache } from "../../../apolloClient"
import { AppContext } from "Views/Home/context"
import { useMark } from "Hooks/basic"
import request from "Methods/request"
const config: Partial<Config> = {
    useCdnDomain: true,
}

const putExtra: Partial<Extra> = {
}


// 七牛token
export function useFetchSyncToken() {
    const client = useApolloClient()
    const fetchToken = async () => {
        try {
            const data: any = cache.readQuery({
                query: gql`
                    query qiniu_token {
                        qiniu_token @client {
                            token
                        }
                    }
                `,
            }) || {}
            if (data) {
                return data.qiniu_token.token
            }
        } catch (e) {
            const { data } = await request.get("/apis/qiniu/token")
            const { token } = data
            // data.token = "test"
            client.writeData({
                data: {
                    qiniu_token: {
                        __typename: "qiniu_token",
                        ...data,
                    },
                }
            })
            return token
        }
    }
    const reWriteToken = async () => {
        try {
            const { data } = await request.get("/apis/qiniu/token")
            const { token } = data
            client.writeData({
                data: {
                    qiniu_token: {
                        __typename: "qiniu_token",
                        ...data,
                    },
                }
            })
            return token
        } catch (e) {
        }
    }
    return { fetchToken, reWriteToken }
}

export interface CustomFile extends File {
    uploaded: boolean,
    url?: string,
    status: string,
}

// 选择文件
export function useSelectFiles(multiple = true, accept?: string) {
    const [files, setFiles] = useState<CustomFile[]>([])
    const handleSelectFiles = () => {
        const input = document.createElement("input")
        input.type = "file"
        input.multiple = multiple
        if (accept) {
            input.accept = accept
        }
        input.onchange = () => {
            if (input !== null && input.files) {
                const newFiles = Object.values(input.files).map(
                    (file: File) => (Object.assign(file, { uploaded: false, status: "pending" })
                    ))
                setFiles(files.concat(newFiles))
            }
        }
        input.click()
    }
    const handleRemoveFile = (index: number) => {
        files.splice(index, 1)
        setFiles([...files])
    }
    const handleChangeFile = (index: number, newFile: CustomFile) => {
        files.splice(index, 1, newFile)
        setFiles([...files])
    }
    const handleResetFile = () => {
        setFiles([])
    }
    return { handleSelectFiles, handleRemoveFile, handleChangeFile, handleResetFile, files }
}
// 管理文件列表
export function useFilesManage(
    files: CustomFile[],
    upload: (
        file: CustomFile,
        onComplete: (file: CustomFile) => void,
        onError: () => void,
    ) => void,
    setFile: (index: number, file: CustomFile) => void,
    removeFile: (index: number) => void,
) {
    const {
        mark: currentUpload,
        handleChangeMark: handleChangeCurrent,
    } = useMark(-1)
    const [uploading, setUploading] = useState(false)
    const [totalSize, setTotalSize] = useState<number>(0)
    const { pendingFiles, uploadedFiles } = useMemo(() => {
        let total = 0
        const uploadedFiles: CustomFile[] = []
        const pendingFiles: CustomFile[] = []
        files.forEach((file: CustomFile) => {
            if (!file.uploaded) {
                pendingFiles.push(file)
            } else {
                // setTotalSize(totalSize + Number(file.size))
                uploadedFiles.push(file)
            }
        })
        uploadedFiles.forEach((file: CustomFile) => {
            total += file.size
        })
        setTotalSize(Number(total))
        handleChangeCurrent(files.length === 0 ? -1 : uploadedFiles.length)
        return { pendingFiles, uploadedFiles }
        // eslint-disable-next-line
    }, [files])

    useEffect(() => {
        if (pendingFiles[0]) {
            setUploading(true)
            upload(
                pendingFiles[0],
                (file: CustomFile) => {
                    setTimeout(() => {
                        setUploading(false)
                    }, 200)
                    setFile(currentUpload, file)
                },
                () => {
                    setTimeout(() => {
                        setUploading(false)
                    }, 200)
                    removeFile(currentUpload)
                },
            )
        } else {
            handleChangeCurrent(-1)
        }
        // eslint-disable-next-line
    }, [currentUpload])
    return { totalSize, uploading, currentUpload, uploadedFiles }
}
// 上传文件
export function useUpload() {
    const [percent, setPercent] = useState()
    const { fetchToken, reWriteToken } = useFetchSyncToken()
    const { handleChangeMessage } = useContext(AppContext)
    const upload = async (
        file: CustomFile,
        onComplete: (file: CustomFile) => void,
        onError: () => void,
    ) => {
        if (file && !file.uploaded) {
            const token = await fetchToken()
            const observable = qiniu.upload(file, null, token, putExtra, config)
            const observer = {
                next(res: Next) {
                    const { total } = res
                    const { percent } = total
                    setPercent(percent)
                },
                error(err: Error) {
                    reWriteToken()
                    handleChangeMessage({
                        show: true,
                        type: "error",
                        text: "token过期，请重新选择文件上传",
                    })
                    onComplete(Object.assign(file, {
                        uploaded: true,
                        status: "error",
                        url: ``,
                    }))
                    onError()
                },
                complete(res: CompletedResult) {
                    const data: any = cache.readQuery({
                        query: gql`
                            query qiniu_token {
                                qiniu_token @client {
                                    domain
                                }
                            }
                        `,
                    }) || {}
                    onComplete(Object.assign(file, {
                        uploaded: true,
                        status: "success",
                        url: `//${data.qiniu_token.domain}/${res.key}`,
                    }))
                },
            }
            observable.subscribe(observer)
        }
    }
    return { percent, upload }
}
// 下载文件
export function useDownload() {
    const handleDownload = (href: string | undefined, name = "图片.png") => {
        if (href) {
            const a = document.createElement("a")
            a.href = `${href}?attname=${encodeURIComponent(name)}`
            a.target = "_blank"
            a.download = `${name}`
            a.click()
        }
    }
    return { handleDownload }
}
